// import InteractionNodes from "../models/interactions/interactionNodes";

import Interaction from "../models/interactions/Interaction";
// import Interactions from "../models/interactions/interactions";
import { Dictionary } from "@reduxjs/toolkit";
import InteractionNode from "../models/interactions/InteractionNode";

// interactions are like Nodes in survey builder
// "Nodes" in this system are the links between interactions, that define the relationship between interactions

// https://stackoverflow.com/questions/29232324/creating-a-nested-json-from-a-flat-json-using-javascript-jquery
export function transformDataFlatToNested(
  rootInteractionID: string,
  interactionNodes: Array<InteractionNode>,
  interactions: Array<Interaction>,
  dictionary: Dictionary<any>,
) {
  // this will always be provided
  // TODO could be any id, but in this case it's 0 for testing

  const interactionNodesDeepCopy = JSON.parse(JSON.stringify(interactionNodes)); // make a deep copy so we're not mutating original state refs
  const interactionsDeepCopy = JSON.parse(JSON.stringify(interactions)); // make a deep copy so we're not mutating original state refs

  Object.keys(interactionNodesDeepCopy).forEach((key) => {
    // grab the node data for the interaction
    const thisNode = interactionNodesDeepCopy[key];

    // grab the interaction corresponding to the nodeID, and store that
    const thisInteraction = interactionsDeepCopy[thisNode.child_interaction_id];
    dictionary[thisInteraction.id] = thisInteraction;

    // get all direct children of this node
    Object.keys(interactionNodesDeepCopy).forEach((key) => {
      const tempThisNode = interactionNodesDeepCopy[key];
      const tempThisInteraction = interactionsDeepCopy[tempThisNode.child_interaction_id];
    });

    // parse through all nodes
    // -if a node has a parent listed
    //    -grab that parent interaction from the dict
    //         -if no children currently added to parent, make new array
    //    -push chilcren to children prop on parent interaction (not node)
    if (thisNode.parent_interaction_id) {
      const parent = dictionary[thisNode.parent_interaction_id];
      if (parent) {
        if (!parent.children) {
          parent.children = [];
        }
        parent.children.push(thisInteraction);
      }
    }
  });

  // delete the remaining child items that are still appearing as flat refs on the top-level of the dict
  // TODO - there's probably a better/more eloquent/more efficient way to handle this in the code above
  //        instead of after the fact
  // console.log(interactionNodesDeepCopy);
  Object.keys(interactionNodesDeepCopy).forEach((key) => {
    const thisNode = interactionNodesDeepCopy[key];
    const thisInteraction = interactionsDeepCopy[thisNode.child_interaction_id];

    if (thisNode.parent_interaction_id !== rootInteractionID) {
      // https://www.tutorialspoint.com/Remove-elements-from-a-Dictionary-using-Javascript
      delete dictionary[thisInteraction.id];
    }
  });

  // console.log("Returned Results from transformDataFlatToNested(): ");
  // console.log(dictionary);
  return dictionary;
}

export function addNestedChildrenToArray(obj: any, resultArray: any) {
  resultArray.push(obj.id);
  obj.children.forEach((child: any) =>
    addNestedChildrenToArray(child, resultArray),
  );
}

// produces an array of InteractionNodes that are children of the given parent
export function getChildrenForFlatInteraction(
  interactionNodeID: string,
  interactionNodes: Array<InteractionNode>,
  interactions: Array<Interaction>,
) {
  // this will always be provided
  // TODO could be any id, but in this case it's 0 for testing
  // interactionNodeID = "1";
  const interactionNodesDeepCopy = stateDeepCopy(interactionNodes); // make a deep copy so we're not mutating original state refs
  const interactionsDeepCopy = stateDeepCopy(interactions); // make a deep copy so we're not mutating original state refs

  const resultArray: Array<InteractionNode | null> = [];

  // // grab the node data for the interaction
  const thisNode = interactionNodesDeepCopy[interactionNodeID];

  // // grab the interaction corresponding to the nodeID, and store that
  // thisInteraction.id is the parent we need to match
  const thisInteraction = interactionsDeepCopy[thisNode.child_interaction_id];

  // get all direct children of this node
  Object.keys(interactionNodesDeepCopy).forEach((key) => {
    const tempThisNode = interactionNodesDeepCopy[key];
    // let tempThisInteraction = interactionsDeepCopy[tempThisNode.child]

    if (parseInt(thisInteraction.id) === parseInt(tempThisNode.parent_interaction_id)) {
      // console.log("TRUE")
      // dictionary[thisInteraction.id] = thisInteraction;
      resultArray.push(tempThisNode);
    }
  });

  return resultArray;
}

// produces an array of Interactionsthat from a flat array of its corresponding InteractionNodes
export function interactionNodeArrayToInteractionsArray(
  interactionNodes: Array<InteractionNode | null>,
  interactions: Array<Interaction>,
): Array<Interaction | null> {
  const interactionNodesDeepCopy = stateDeepCopy(interactionNodes); // make a deep copy so we're not mutating original state refs
  const interactionsDeepCopy = stateDeepCopy(interactions); // make a deep copy so we're not mutating original state refs
  const interactionsReturnArray: Array<Interaction | null> = [];
  Object.keys(interactionNodesDeepCopy).forEach((key) => {
    const tempThisNode = interactionNodesDeepCopy[key];
    const thisInteraction = interactionsDeepCopy[tempThisNode.child_interaction_id];
    interactionsReturnArray.push(thisInteraction);
  });

  return interactionsReturnArray;
}

export function getNodeForInteractionID(
  interactionID: string,
  interactionNodes: any,
) {
  const interactionNodesDeepCopy = stateDeepCopy(interactionNodes); // make a deep copy so we're not mutating original state refs
  let returnNode = interactionNodes[0];
  Object.keys(interactionNodesDeepCopy).forEach((key) => {
    const tempThisNode = interactionNodesDeepCopy[key];
    if (tempThisNode.child_interaction_id === interactionID) {
      returnNode = tempThisNode;
    }
  });
  return returnNode;
}

export function getInteractionForNodeID(
  nodeID: string,
  interactionNodes: any,
  interactions: any,
) {
  const interactionNodesDeepCopy = stateDeepCopy(interactionNodes); // make a deep copy so we're not mutating original state refs
  let returnInteraction = interactions[0];
  Object.keys(interactionNodesDeepCopy).forEach((key) => {
    const tempThisNode = interactionNodesDeepCopy[key];
    if (tempThisNode.id === nodeID) {
      returnInteraction = interactions[tempThisNode.child_interaction_id];
    }
  });
  return returnInteraction;
}

// turns obj of type InteractionNodes into Array<InteractionNode | null >
export function interactionNodesToInteractionNodesArray(
  interactionNodes: Array<InteractionNode>,
): Array<InteractionNode | null> {
  const interactionNodesDeepCopy = stateDeepCopy(interactionNodes); // make a deep copy so we're not mutating original state refs
  const interactionsReturnArray: Array<InteractionNode | null> = [];
  Object.keys(interactionNodesDeepCopy).forEach((key) => {
    interactionsReturnArray.push(interactionNodesDeepCopy[key]);
  });
  return interactionsReturnArray;
}

// this is broken right now
// troubleshoot - https://stackoverflow.com/questions/72904920/dispatch-a-function-with-redux-and-typescript-not-working
export const addChildrenTFlatNodesTHUNK = (
  interactionNodes: Array<InteractionNode>,
  interactions: Array<Interaction>,
) => {
  // const dispatch = useStoreDispatch();

  const interactionNodesDeepCopy = JSON.parse(JSON.stringify(interactionNodes)); // make a deep copy so we're not mutating original state refs

  // get all direct children of this node
  Object.keys(interactionNodesDeepCopy).forEach((key) => {
    const tempThisNode = interactionNodesDeepCopy[key];

    const children = getChildrenForFlatInteraction(
      tempThisNode.id,
      interactionNodes,
      interactions,
    );
    // console.log(children)
    interactionNodesDeepCopy[key].children = children;

    // this isn't working right now? will need to fix:
  });

  // console.log(interactionNodesDeepCopy);
  return interactionNodesDeepCopy;
};

export const addChildrenTFlatNodes = (
  interactionNodes: Array<InteractionNode>,
  interactions: Array<Interaction>,
) => {
  // const dispatch = useStoreDispatch();

  const interactionNodesDeepCopy = JSON.parse(JSON.stringify(interactionNodes)); // make a deep copy so we're not mutating original state refs

  // get all direct children of this node
  Object.keys(interactionNodesDeepCopy).forEach((key) => {
    const tempThisNode = interactionNodesDeepCopy[key];

    // let tempThisInteraction = interactionsDeepCopy[tempThisNode.child]

    const children = getChildrenForFlatInteraction(
      tempThisNode.id,
      interactionNodes,
      interactions,
    );
    // console.log(children)
    interactionNodesDeepCopy[key].children = children;

    // this isn't working right now? will need to fix:
  });

  // console.log(interactionNodesDeepCopy);
  return interactionNodesDeepCopy;
};

export const addInteractionsToInteractionNodes = (
  interactionNodes: Array<InteractionNode>,
  interactions: Array<Interaction>,
) => {
  // const dispatch = useStoreDispatch();

  const interactionNodesDeepCopy = JSON.parse(JSON.stringify(interactionNodes)); // make a deep copy so we're not mutating original state refs
  const interactionsDeepCopy = JSON.parse(JSON.stringify(interactions)); // make a deep copy so we're not mutating original state refs

  // get all direct children of this node
  Object.keys(interactionNodesDeepCopy).forEach((key) => {
    const tempThisNode = interactionNodesDeepCopy[key];
    const tempThisInteraction = interactionsDeepCopy[tempThisNode.child_interaction_id];
    interactionNodesDeepCopy[key].interaction = tempThisInteraction;
  });

  // console.log(interactionNodesDeepCopy);
  return interactionNodesDeepCopy;
};

export function stateDeepCopy(state: any) {
  const stateDeepCopy = JSON.parse(JSON.stringify(state)); // make a deep copy so we're not mutating original state refs
  return stateDeepCopy;
}
